import { useTellescopeQuery } from "@tellescope/tanstack-query";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  ButtonProps,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { LucideIcon } from "lucide-react";
import { useMemo } from "react";
import { Fragment } from "react/jsx-runtime";

interface ActionButtonConfig extends Omit<ButtonProps, "children" | "variant" | "size" | "hoverLabel" | "onClick"> {
  destructive?: boolean;
  hoverLabel: string;
  icon: LucideIcon;
  onClick: NonNullable<ButtonProps["onClick"]>;
}

export interface ContactDetailsSectionBreadcrumbsProps {
  enduserId: string;
  breadcrumbs: { title: string; onClick?: () => void }[];
  actionButtons?: ActionButtonConfig[];
  maxDisplayCount?: number;
}

export const ContactDetailsSectionBreadcrumbs = ({
  enduserId,
  breadcrumbs,
  actionButtons,
  maxDisplayCount = 1,
}: ContactDetailsSectionBreadcrumbsProps) => {
  const enduserNameQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId,
    enabled: !!enduserId,
    select: (data) => formatName(data),
  });

  const { breadcrumbsToDisplay, breadcrumbsToRollup } = useMemo(() => {
    return {
      breadcrumbsToDisplay: breadcrumbs.slice(maxDisplayCount * -1),
      breadcrumbsToRollup: breadcrumbs.slice(0, maxDisplayCount * -1),
    };
  }, [breadcrumbs, maxDisplayCount]);

  return (
    <div className="flex justify-between items-center min-h-9 gap-1">
      <Breadcrumb className="flex-shrink min-w-0">
        <BreadcrumbList className="sm:gap-1">
          <BreadcrumbItem title={enduserNameQuery.data} className="truncate">
            <BreadcrumbPage className="font-medium">{enduserNameQuery.data}</BreadcrumbPage>
          </BreadcrumbItem>
          {!!breadcrumbsToRollup.length && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm-icon">
                      <BreadcrumbEllipsis />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {breadcrumbsToRollup.map((crumb) => (
                      <DropdownMenuItem
                        key={crumb.title}
                        onClick={crumb.onClick}
                        className={crumb.onClick ? "cursor-pointer" : "cursor-default"}
                        title={crumb.title}
                      >
                        {crumb.title}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </BreadcrumbItem>
            </>
          )}
          {breadcrumbsToDisplay.map((crumb, i) => (
            <Fragment key={crumb.title}>
              <BreadcrumbSeparator />
              <BreadcrumbItem
                title={crumb.title}
                onClick={i === breadcrumbsToDisplay.length - 1 ? undefined : crumb.onClick}
              >
                <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
      {!!actionButtons?.length && (
        <div className="flex gap-1 items-center">
          {actionButtons.map(({ icon: Icon, hoverLabel, ...rest }) => (
            <Button variant="ghost" size="sm-icon" key={hoverLabel} hoverLabel={hoverLabel} {...rest}>
              <Icon />
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
